import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { submitTutorApplication } from "@/services/tutorApplicationService";
import { getErrorMessage } from "@/services/apiClient";
import { ROUTE } from "@/route/app_routes.js";

/**
 * "Apply to teach" popup opened from the homepage's TutorRecruitment.js section. Collects a
 * public tutor application (no auth required) and posts it via tutorApplicationService.js to
 * the Laravel backend's /v1/user/tutor-applications route.
 *
 * Reuses the shared .rbt-modal-overlay/.rbt-modal shell (ConfirmModal.js, ReportEventButton.js,
 * DeleteAccountModal.js) with a `.rbt-tutor-apply-modal` width/scroll modifier defined in
 * public/css/pages/TutorRecruitment.css, and the .rbt-dash-field input styling already used
 * inside modals elsewhere (ReportEventButton.js).
 *
 * The required "Terms of Use" link points at /terms#tutor-content -- the specific section added
 * to TermsOfService.js explaining that a tutor relinquishes rights to a course once it's
 * published, so an applicant can read exactly what they're agreeing to before checking the box.
 */
const TutorApplicationModal = ({ open, onClose }) => {
  const { t } = useTranslation("home");
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState(null);

  const applySchema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t("tutorRecruitment.modal.validation.nameRequired")).max(150),
        email: z
          .string()
          .trim()
          .min(1, t("tutorRecruitment.modal.validation.emailRequired"))
          .email(t("tutorRecruitment.modal.validation.emailInvalid")),
        whatsapp: z.string().trim().min(1, t("tutorRecruitment.modal.validation.whatsappRequired")).max(30),
        courseTitle: z.string().trim().min(1, t("tutorRecruitment.modal.validation.courseTitleRequired")).max(255),
        youtubeChannel: z
          .string()
          .trim()
          .min(1, t("tutorRecruitment.modal.validation.youtubeChannelRequired"))
          .url(t("tutorRecruitment.modal.validation.youtubeChannelInvalid")),
        instagram: z
          .union([z.string().trim().url(t("tutorRecruitment.modal.validation.socialInvalid")), z.literal("")])
          .optional(),
        twitter: z
          .union([z.string().trim().url(t("tutorRecruitment.modal.validation.socialInvalid")), z.literal("")])
          .optional(),
        about: z.string().trim().min(1, t("tutorRecruitment.modal.validation.aboutRequired")).max(5000),
        agreeTerms: z.boolean().refine((v) => v === true, {
          message: t("tutorRecruitment.modal.validation.agreeTermsRequired"),
        }),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(applySchema),
    defaultValues: {
      name: "",
      email: "",
      whatsapp: "",
      courseTitle: "",
      youtubeChannel: "",
      instagram: "",
      twitter: "",
      about: "",
      agreeTerms: false,
    },
  });

  // Reset to a clean form every time the modal is (re)opened, so a previous submission's data
  // never lingers if the visitor closes and reopens it.
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setServerError(null);
      reset();
    }
  }, [open, reset]);

  if (!open) return null;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const onSubmit = async (values) => {
    setServerError(null);
    try {
      await submitTutorApplication({
        name: values.name,
        email: values.email,
        whatsapp: values.whatsapp,
        course_title: values.courseTitle,
        youtube_channel: values.youtubeChannel,
        instagram: values.instagram || null,
        twitter: values.twitter || null,
        about: values.about,
      });
      setSubmitted(true);
      toast.success(t("tutorRecruitment.modal.successTitle", "Application received"));
    } catch (error) {
      const message = getErrorMessage(error);
      setServerError(message);
      toast.error(message);
    }
  };

  return (
    <div className="rbt-modal-overlay" role="dialog" aria-modal="true" onClick={handleClose}>
      <div className="rbt-modal rbt-tutor-apply-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="rbt-tutor-apply-modal__close"
          onClick={handleClose}
          disabled={isSubmitting}
          aria-label={t("tutorRecruitment.modal.closeAriaLabel", "Close")}
        >
          <i className="feather-x"></i>
        </button>

        {submitted ? (
          <div className="rbt-tutor-apply-modal__success">
            <div className="rbt-modal__icon">
              <i className="feather-check-circle"></i>
            </div>
            <h4 className="rbt-modal__title">{t("tutorRecruitment.modal.successTitle", "Application received")}</h4>
            <p className="rbt-modal__message">
              {t(
                "tutorRecruitment.modal.successMessage",
                "Thanks for applying — our team reviews every submission personally and will reach out by email or WhatsApp."
              )}
            </p>
            <button type="button" className="rbt-btn btn-gradient radius-round" onClick={handleClose}>
              {t("tutorRecruitment.modal.doneButton", "Done")}
            </button>
          </div>
        ) : (
          <>
            <h4 className="rbt-modal__title rbt-tutor-apply-modal__title">{t("tutorRecruitment.modal.title", "Apply to teach")}</h4>
            <p className="rbt-modal__message rbt-tutor-apply-modal__subtitle">
              {t("tutorRecruitment.modal.subtitle", "Tell us about yourself and the course you'd like to build.")}
            </p>

            <div className="rbt-legal-callout rbt-tutor-apply-modal__disclaimer">
              <i className="feather-info" aria-hidden="true"></i>
              <p>
                {t(
                  "tutorRecruitment.modal.disclaimer",
                  "SmartTech Academy helps people around the world upskill for free. In return for creating quality, real-skill content, you get credit for your work and growing exposure as our learner base expands. We review every submitted course for quality and value, and we reserve the right to decline work that doesn't meet our bar — a pass here isn't a reflection on you personally."
                )}
              </p>
            </div>

            {serverError && (
              <div className="rbt-auth-split__alert" role="alert">
                <i className="feather-alert-circle"></i>
                <span>{serverError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="rbt-tutor-apply-modal__form">
              <div className="rbt-dash-field">
                <label htmlFor="tutor-apply-name">{t("tutorRecruitment.modal.nameLabel", "Name")}</label>
                <input id="tutor-apply-name" type="text" autoComplete="name" {...register("name")} />
                {errors.name && <span className="text-danger d-block mt--5">{errors.name.message}</span>}
              </div>

              <div className="rbt-dash-field">
                <label htmlFor="tutor-apply-email">{t("tutorRecruitment.modal.emailLabel", "Email")}</label>
                <input id="tutor-apply-email" type="email" autoComplete="email" {...register("email")} />
                {errors.email && <span className="text-danger d-block mt--5">{errors.email.message}</span>}
              </div>

              <div className="rbt-dash-field">
                <label htmlFor="tutor-apply-whatsapp">{t("tutorRecruitment.modal.whatsappLabel", "WhatsApp number")}</label>
                <input id="tutor-apply-whatsapp" type="tel" autoComplete="tel" {...register("whatsapp")} />
                {errors.whatsapp && <span className="text-danger d-block mt--5">{errors.whatsapp.message}</span>}
              </div>

              <div className="rbt-dash-field">
                <label htmlFor="tutor-apply-course-title">{t("tutorRecruitment.modal.courseTitleLabel", "Course title you want to add")}</label>
                <input id="tutor-apply-course-title" type="text" {...register("courseTitle")} />
                {errors.courseTitle && <span className="text-danger d-block mt--5">{errors.courseTitle.message}</span>}
              </div>

              <div className="rbt-dash-field">
                <label htmlFor="tutor-apply-youtube">{t("tutorRecruitment.modal.youtubeChannelLabel", "YouTube channel link")}</label>
                <input id="tutor-apply-youtube" type="url" placeholder="https://youtube.com/@yourchannel" {...register("youtubeChannel")} />
                {errors.youtubeChannel && <span className="text-danger d-block mt--5">{errors.youtubeChannel.message}</span>}
              </div>

              <div className="rbt-tutor-apply-modal__row">
                <div className="rbt-dash-field">
                  <label htmlFor="tutor-apply-instagram">{t("tutorRecruitment.modal.instagramLabel", "Instagram (optional)")}</label>
                  <input id="tutor-apply-instagram" type="url" placeholder="https://instagram.com/you" {...register("instagram")} />
                  {errors.instagram && <span className="text-danger d-block mt--5">{errors.instagram.message}</span>}
                </div>

                <div className="rbt-dash-field">
                  <label htmlFor="tutor-apply-twitter">{t("tutorRecruitment.modal.twitterLabel", "Twitter / X (optional)")}</label>
                  <input id="tutor-apply-twitter" type="url" placeholder="https://x.com/you" {...register("twitter")} />
                  {errors.twitter && <span className="text-danger d-block mt--5">{errors.twitter.message}</span>}
                </div>
              </div>

              <div className="rbt-dash-field">
                <label htmlFor="tutor-apply-about">{t("tutorRecruitment.modal.aboutLabel", "Tell us about yourself")}</label>
                <textarea
                  id="tutor-apply-about"
                  rows={4}
                  placeholder={t("tutorRecruitment.modal.aboutPlaceholder", "Your background, what you teach, and why you'd be a great fit.")}
                  {...register("about")}
                />
                {errors.about && <span className="text-danger d-block mt--5">{errors.about.message}</span>}
              </div>

              <div className="rbt-checkbox rbt-tutor-apply-modal__agree">
                <input id="tutor-apply-agree" type="checkbox" {...register("agreeTerms")} />
                <label htmlFor="tutor-apply-agree">
                  {t("tutorRecruitment.modal.agreeTermsPrefix", "I agree to SmartTech Academy's")}{" "}
                  <Link href={`${ROUTE.terms}#tutor-content`} target="_blank" rel="noopener noreferrer">
                    {t("tutorRecruitment.modal.agreeTermsLink", "Terms of Use")}
                  </Link>{" "}
                  {t("tutorRecruitment.modal.agreeTermsMiddle", "and")}{" "}
                  <Link href={ROUTE.privacy} target="_blank" rel="noopener noreferrer">
                    {t("tutorRecruitment.modal.agreeTermsPrivacyLink", "Privacy Policy")}
                  </Link>
                </label>
              </div>
              {errors.agreeTerms && <span className="text-danger d-block mt--5">{errors.agreeTerms.message}</span>}

              <div className="rbt-modal__actions rbt-tutor-apply-modal__actions">
                <button type="submit" className="rbt-btn btn-gradient radius-round w-100" disabled={isSubmitting}>
                  {isSubmitting
                    ? t("tutorRecruitment.modal.submittingButton", "Submitting…")
                    : t("tutorRecruitment.modal.submitButton", "Submit application")}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default TutorApplicationModal;
